import { useCallback, useContext, useEffect } from "react";
import { QueryClient, QueryClientContext, useMutation, useQuery } from "@tanstack/react-query";
import { useOptionalApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";

/**
 * The person's first steps, kept by the API (`/api/me/onboarding`, 21 Oct 2026): what the first run,
 * the tour and the first wins read. Timestamps only, never content.
 * reports/2026-10-21-welcome-onboarding.md
 */
export interface Onboarding {
  createdAt: string;
  verified: boolean;
  welcomeSent: boolean;
  firstDocumentAt: string | null;
  firstDocumentRoute: string | null;
  tourStepReached: number | null;
  tourSeen: boolean;
  firstValidatePassAt: string | null;
  firstExportAt: string | null;
  tipsOptOut: boolean;
  /** An account from before onboarding existed: no forced tour, no welcome. */
  legacy: boolean;
  /** The call that returned this recorded its step for the first time. */
  justRecorded?: boolean;
}

export type OnboardingStep =
  | "start" | "verified" | "first-document" | "tour-step" | "tour-seen" | "tour-restart"
  | "first-validate-pass" | "first-export" | "tips-opt-out";

export type FirstDocumentRoute = "blank" | "template" | "import" | "describe" | "shared";

const START_KEY = "lilia.onboarding.start-pending";

interface PendingStart { name?: string; timeZone?: string }

/** At sign-up: the name typed there and the browser's time zone, sent once the account exists. */
export function stashOnboardingStart(name: string) {
  let timeZone: string | undefined;
  try { timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* left to the server */ }
  try {
    window.localStorage.setItem(START_KEY, JSON.stringify({ name: name.trim() || undefined, timeZone } satisfies PendingStart));
  } catch { /* private window: the name comes from Stytch instead */ }
}

function pendingStart(): PendingStart | null {
  try {
    const raw = window.localStorage.getItem(START_KEY);
    return raw ? (JSON.parse(raw) as PendingStart) : null;
  } catch { return null; }
}

function clearPendingStart() {
  try { window.localStorage.removeItem(START_KEY); } catch { /* nothing to clear */ }
}

export const ONBOARDING_KEY = ["me", "onboarding"] as const;

/** Views rendered without the app's providers (tests, isolated panels): onboarding stays unknown. */
const FALLBACK = new QueryClient();

export function useOnboarding() {
  const api = useOptionalApiContext();
  const provided = useContext(QueryClientContext);
  const queryClient = provided ?? FALLBACK;
  const query = useQuery(
    {
      queryKey: ONBOARDING_KEY,
      // Asking also lets the API send the welcome email once the address is verified.
      queryFn: async () => handleResponse<Onboarding>(await api!.fetchWithAuth(`${API_BASE}/me/onboarding`)),
      enabled: !!api && !!provided,
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
    queryClient,
  );
  const record = useMutation(
    {
      mutationFn: async ({ step, body }: { step: OnboardingStep; body?: Record<string, unknown> }) => {
        if (!api) throw new Error("onboarding: no API here");
        return handleResponse<Onboarding>(
          await api.fetchWithAuth(`${API_BASE}/me/onboarding/${step}`, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
        );
      },
      onSuccess: (saved) => queryClient.setQueryData(ONBOARDING_KEY, saved),
    },
    queryClient,
  );
  return { onboarding: query.data, loaded: query.isSuccess, record: record.mutateAsync };
}

/**
 * Notes the person's first document and how it started (blank, template, import, describe). Every
 * route that creates one calls it after success; it posts only while none is noted yet, and the
 * API keeps the first anyway.
 */
export function useNoteFirstDocument() {
  const { onboarding, record } = useOnboarding();
  const noted = !!onboarding?.firstDocumentAt;
  return useCallback((route: FirstDocumentRoute) => {
    if (noted) return;
    record({ step: "first-document", body: { route } }).catch(() => undefined);
  }, [noted, record]);
}

/**
 * A first win (Olivia 1a/1b): records the step and says whether this was the account's first time,
 * so its one-time line shows once. Skips the call when the record already has it.
 */
export function useFirstWin(step: "first-validate-pass" | "first-export") {
  const { onboarding, record } = useOnboarding();
  const had = step === "first-validate-pass" ? !!onboarding?.firstValidatePassAt : !!onboarding?.firstExportAt;
  return useCallback(async (): Promise<boolean> => {
    if (had) return false;
    try {
      return !!(await record({ step })).justRecorded;
    } catch {
      return false;
    }
  }, [had, record, step]);
}

/**
 * Mounted once for a signed-in person: loads the onboarding record (which sends the welcome email
 * when it is due) and hands the API what sign-up stashed.
 */
export function OnboardingSync() {
  const { loaded, record } = useOnboarding();
  useEffect(() => {
    const start = pendingStart();
    if (!loaded || !start) return;
    record({ step: "start", body: { ...start } }).then(clearPendingStart, () => undefined);
  }, [loaded, record]);
  return null;
}
